/**
 * Referral Codes API Client
 *
 * Handles referral code management for organization admins.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 *
 * API Endpoints (from gw-functions/referral-codes Lambda, previously gw-api):
 * - GET /organizations/{org_id}/referral-codes - List referral codes
 * - POST /organizations/{org_id}/referral-codes - Create referral code
 * - GET /organizations/{org_id}/referral-codes/{id} - Get referral code
 * - POST /organizations/{org_id}/referral-codes/{id}/revoke - Revoke referral code
 *
 * @see GW-1842 Referral Codes management page
 * @see GW-1838 REST API endpoints (backend)
 */

import { assertOrgId } from '@/types/orgId';
import type {
  ReferralCode,
  CreateReferralCodeRequest,
  ActivateReferralCodeRequest,
  ActivateReferralCodeResponse,
  ListReferralCodesParams,
  ListReferralCodesResponse,
} from '@/types/referralCode.types';
// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.

import { BaseApiClient } from './baseApiClient';

/**
 * Referral Codes API client class
 * Manages referral code creation, listing, retrieval, and revocation
 */
class ReferralCodesApiClient extends BaseApiClient {
  /**
   * List referral codes for an organization
   *
   * HTTP Method: GET /organizations/:orgId/referral-codes
   * Authorization: Required (JWT token)
   * RBAC: org_admin
   *
   * @param orgId - Organization ID
   * @param params - Optional query parameters for filtering and pagination
   * @returns List of referral codes with pagination
   */
  async listReferralCodes(
    orgId: string,
    params?: ListReferralCodesParams
  ): Promise<ListReferralCodesResponse> {
    assertOrgId(orgId);
    const queryParams: Record<string, string | number | boolean | string[] | undefined> = {};

    if (params?.status) {
      queryParams.status = params.status;
    }
    if (params?.limit) {
      queryParams.limit = params.limit;
    }
    if (params?.nextToken) {
      queryParams.next_token = params.nextToken;
    }

    const queryString = this.buildQueryString(queryParams);
    const path = `/organizations/${orgId}/referral-codes${queryString}`;

    // Backend uses snake_case for the response field names
    const rawResponse = await this.get<{
      referral_codes: ReferralCode[];
      total: number;
      next_token?: string;
    }>(path);

    return {
      referralCodes: rawResponse.referral_codes ?? [],
      total: rawResponse.total,
      nextToken: rawResponse.next_token,
    };
  }

  /**
   * Get a specific referral code by ID
   *
   * HTTP Method: GET /organizations/:orgId/referral-codes/:referralCodeId
   * Authorization: Required (JWT token)
   * RBAC: org_admin
   *
   * @param orgId - Organization ID
   * @param referralCodeId - Referral code ID
   * @returns Referral code details
   */
  async getReferralCode(orgId: string, referralCodeId: string): Promise<ReferralCode> {
    assertOrgId(orgId);
    return this.get<ReferralCode>(`/organizations/${orgId}/referral-codes/${referralCodeId}`);
  }

  /**
   * Create a new referral code
   *
   * HTTP Method: POST /organizations/:orgId/referral-codes
   * Authorization: Required (JWT token)
   * RBAC: org_admin
   *
   * @param orgId - Organization ID
   * @param request - Referral code creation parameters
   * @returns Created referral code
   */
  async createReferralCode(
    orgId: string,
    request: CreateReferralCodeRequest
  ): Promise<ReferralCode> {
    assertOrgId(orgId);
    return this.post<ReferralCode>(`/organizations/${orgId}/referral-codes`, request);
  }

  /**
   * Revoke a referral code
   *
   * HTTP Method: POST /organizations/:orgId/referral-codes/:referralCodeId/revoke
   * Authorization: Required (JWT token)
   * RBAC: org_admin
   *
   * @param orgId - Organization ID
   * @param referralCodeId - Referral code ID to revoke
   */
  async revokeReferralCode(orgId: string, referralCodeId: string): Promise<void> {
    assertOrgId(orgId);
    await this.post<void>(`/organizations/${orgId}/referral-codes/${referralCodeId}/revoke`);
  }

  /**
   * Activate a referral code for the current user/organization.
   *
   * Called during login when a `gw_ref` cookie is present.
   * Creates a referral attribution linking the current user's org
   * to the referral code creator for revenue sharing.
   *
   * HTTP Method: POST /referral-codes/activate
   * Authorization: Required (JWT token)
   *
   * @param request - The activation request containing the referral code ID
   * @returns Activation response with attribution details
   *
   * @see GW-1853 Referral cookie attribution on login
   * @see GW-1844 Attribution on signup/login (backend)
   */
  async activateReferralCode(
    request: ActivateReferralCodeRequest
  ): Promise<ActivateReferralCodeResponse> {
    return this.post<ActivateReferralCodeResponse>('/referral-codes/activate', request);
  }
}

/**
 * Singleton instance export
 */
export const referralCodesApi = new ReferralCodesApiClient();
